import { environment } from '../env'
import * as d3 from 'd3'
import { UNITS } from '../types/misc.types'
import { getAuthHeaders } from './sessionStorage'
import { androidUpdateApp, iosUpdateApp, RUPEE_SYMBOL } from '../constants'
import { addBreadcrumb, captureException } from '@sentry/react'

// ESign logging enums
export enum ESignLogMessage {
  APPLICATION_STARTED = 'Application started - main.tsx loaded',
  APP_INITIALIZATION_STARTED = 'App initialization started',
  AUTHENTICATION_COMPLETED = 'Authentication completed',
  AUTH_TIMEOUT_FALLBACK = 'Auth timeout - rendering fallback page',
  APP_COMPONENT_MOUNTED = 'App component mounted - routing initialized',
  ESIGN_ROUTE_MATCHED = 'ESign route matched - ESignPage will render',
  ESIGN_PAGE_CREATED = 'ESignPage component created/rendered',
  ESIGN_PAGE_MOUNTED = 'ESignPage component mounted and API call initiated',
  ESIGN_API_SUCCESS = 'ESignPage API call successful',
  ESIGN_API_FAILED = 'ESignPage API call failed',
  ESIGN_API_TIMEOUT = 'ESignPage API call timeout - no response after 12 seconds',
  APP_INITIALIZATION_FAILED = 'App initialization failed - rendering fallback',
  ESIGN_FLOW_TIMEOUT = 'ESign flow timeout - page never reached after 15 seconds'
}

export enum ESignLogLevel {
  INFO = 'info',
  ERROR = 'error',
  WARNING = 'warning'
}

// Global state for ESign flow tracking
let esignFlowStartTime: number | null = null
let esignFlowTimeoutId: NodeJS.Timeout | null = null
let esignPageReached = false

// Initialize ESign flow tracking
export const initializeESignFlowTracking = (): void => {
  try {
    if (!window?.location?.pathname?.includes('esign')) {
      return
    }

    // Start tracking the flow
    esignFlowStartTime = Date.now()
    esignPageReached = false

    // Set timeout to catch if page never loads
    esignFlowTimeoutId = setTimeout(() => {
      if (!esignPageReached) {
        const flowDuration = esignFlowStartTime ? Date.now() - esignFlowStartTime : 0

        captureException(new Error(ESignLogMessage.ESIGN_FLOW_TIMEOUT), {
          tags: {
            component: 'ESignPage',
            errorType: 'esign_flow_timeout'
          },
          extra: {
            pathname: window?.location?.pathname,
            search: window?.location?.search,
            hash: window?.location?.hash,
            fullUrl: window?.location?.href,
            userAgent: navigator?.userAgent,
            timestamp: new Date()?.toISOString(),
            environment: environment,
            flowDurationMs: flowDuration,
            esignPageReached: false
          }
        })
      }
    }, 15000) // 15 second timeout
  } catch (error) {
    console.error('ESign flow tracking initialization failed:', error)
  }
}

// Mark that ESignPage has been reached
export const markESignPageReached = (): void => {
  try {
    esignPageReached = true
    if (esignFlowTimeoutId) {
      clearTimeout(esignFlowTimeoutId)
      esignFlowTimeoutId = null
    }
  } catch (error) {
    console.error('Failed to mark ESign page as reached:', error)
  }
}

// Common ESign logging function
export const logESignEvent = (
  message: ESignLogMessage,
  level: ESignLogLevel = ESignLogLevel.INFO,
  additionalData: Record<string, any> = {}
): void => {
  try {
    // Only log if it's an esign route
    if (!window?.location?.pathname?.includes('esign')) {
      return
    }

    const baseData = {
      pathname: window?.location?.pathname,
      search: window?.location?.search,
      hash: window?.location?.hash,
      fullUrl: window?.location?.href,
      userAgent: navigator?.userAgent,
      timestamp: new Date()?.toISOString(),
      environment: environment,
      flowDurationMs: esignFlowStartTime ? Date.now() - esignFlowStartTime : 0,
      esignPageReached,
      ...additionalData
    }

    // Add breadcrumb for all events
    addBreadcrumb({
      message,
      level,
      data: baseData
    })

    // Add exception for error level events
    if (level === ESignLogLevel.ERROR) {
      captureException(new Error(message), {
        tags: {
          component: 'ESignPage',
          errorType: 'esign_flow_error'
        },
        extra: baseData
      })
    }
  } catch (error) {
    // Fallback logging if Sentry fails
    console.error('ESign logging failed:', error, {
      originalMessage: message,
      level,
      additionalData
    })
  }
}

export function shouldShowDebugTool(): boolean {
  if (!isProdEnvironment()) return true
  return false
}

export function isDevEnvironment() {
  return environment === 'development'
}

export function isStageEnvironment() {
  return environment === 'stage'
}

export function isProdEnvironment() {
  return environment === 'prod'
}

/**
 * Tells the index.html boot watcher how far startup got. Anything short of `ready` after 20s visible is reported.
 * The first `failed` sticks so its boundary and error are what the watcher reports.
 */
export function markWebviewBoot(
  state: 'loader' | 'auth-timeout' | 'auth-ok' | 'ready' | 'failed',
  detail?: {
    message?: 'webview_failed_during_init' | 'webview_failed_in_app' | 'webview_failed_in_route'
    step?: 'init' | 'render' | 'token' | 'secondary_token'
    error?: unknown
  }
): void {
  try {
    const root = document.documentElement
    if (!root) return
    const existingBoot = root.getAttribute('data-boot')
    const existingMessage = root.getAttribute('data-boot-message')
    // First failure message wins. A later stage or ready must not erase it.
    if (existingMessage) return
    // A healthy boot stays ready until an error screen replaces it.
    if (existingBoot === 'ready' && state !== 'failed') return
    // failed with no message yet can be filled in once. Anything else sticks.
    if (existingBoot === 'failed' && state !== 'failed') return
    if (existingBoot === 'failed' && !detail?.message) return

    root.setAttribute('data-boot', state)
    if (detail?.step) root.setAttribute('data-boot-step', detail.step)
    else if (state === 'failed' || state === 'ready') root.removeAttribute('data-boot-step')
    if (detail?.message) root.setAttribute('data-boot-message', detail.message)
    // Kept after later stages, so a failure still shows whether auth had timed out.
    if (state === 'auth-timeout') root.setAttribute('data-boot-auth', 'timeout')
    if (state === 'auth-ok') root.setAttribute('data-boot-auth', 'ok')
    const errorText = bootErrorText(detail?.error)
    if (errorText) root.setAttribute('data-boot-error', errorText)
    const errorStackText = bootErrorStack(detail?.error)
    if (errorStackText) root.setAttribute('data-boot-stack', errorStackText)
  } catch (error) {
    console.error('[webview-health] failed to mark boot', state, error)
  }
}

function bootErrorText(error: unknown): string {
  if (typeof error === 'string') return error.slice(0, 200)
  if (error instanceof Error && error.message) return error.message.slice(0, 200)
  return ''
}

function bootErrorStack(error: unknown): string {
  if (error instanceof Error && error.stack) return error.stack.slice(0, 1500)
  return ''
}

export function normalizePhoneNumber(phone: string): string {
  return phone.replace(/\D/g, '')
}

export const throttle = (func: Function, delay: number) => {
  let lastCall = 0
  return (...args: any[]) => {
    const now = Date.now()
    if (now - lastCall >= delay) {
      func(...args)
      lastCall = now
    }
  }
}

export const formatNumberWithoutCurrency = (number: number, locale = 'en-IN'): number | string => {
  return isNaN(number)
    ? 0
    : (() => {
        const formattedNumber = Math.abs(number)
        return `${new Intl.NumberFormat(locale).format(formattedNumber) || formattedNumber}`
      })()
}

export const roundOffAndFormatNumberWithCurrency = (
  num: number,
  showDecimalsForSmallNumbers = false,
  roundingType: 'round' | 'floor' | 'ceil' = 'round'
): number | string => {
  if (typeof num === 'number') {
    if (showDecimalsForSmallNumbers && num < 1) {
      return fixedDigitsAndFormatNumberWithCurrency(num, 2)
    }
    let formattedNumber = Math.round(Math.abs(num))
    if (roundingType === 'floor') {
      formattedNumber = Math.floor(Math.abs(num))
    } else if (roundingType === 'ceil') {
      formattedNumber = Math.ceil(Math.abs(num))
    }
    return `${RUPEE_SYMBOL}${formatNumberWithoutCurrency(+formattedNumber)}`
  }
  return 0
}

export const fixedDigitsAndFormatNumberWithCurrency = (num: number, fixed: number): number | string => {
  if (typeof num === 'number') {
    const formattedNumber = toFixed(Math.abs(num), fixed)
    return `${RUPEE_SYMBOL}${formatNumberWithoutCurrency(+formattedNumber)}`
  }
  return 0
}

// export function toFixed(num: number, fixed: number) {
//   var re = new RegExp('^-?\\d+(?:.\\d{0,' + (fixed || -1) + '})?')
//   return num?.toString()?.match?.(re)?.[0] ?? ''
// }

export function toFixed(num: number, fixed: number) {
  if (typeof num === 'number') return num?.toFixed(fixed)
  return 0
}

const HUNDRED_CRORES = 1000000000 //100,00,00,000
const TEN_CRORES = 100000000 // 10,00,00,000
const ONE_CRORE = 10000000 // 1,00,00,000
const TEN_LAKHS = 1000000 // 10,00,000
const ONE_LAKH = 100000 // 1,00,000

export function convertNumberToCurrency({
  amount,
  isRound,
  isSmall,
  casing
}: {
  amount: number
  isRound?: boolean
  isSmall?: boolean
  casing?: 'LOWER' | 'PASCAL' | 'UPPER'
}) {
  const val = Math.abs(amount)

  // Helper function to apply casing
  const applyCasing = (text: string): string => {
    if (!casing) return text
    switch (casing) {
      case 'LOWER':
        return text.toLowerCase()
      case 'UPPER':
        return text.toUpperCase()
      case 'PASCAL':
        return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
      default:
        return text
    }
  }

  if (val >= TEN_LAKHS) {
    let digits = 2
    if (val >= HUNDRED_CRORES) digits = 0
    else if (val >= TEN_CRORES) digits = 1
    else if (val >= TEN_LAKHS) digits = 2

    const croreValue = isRound ? Math.round(val / ONE_CRORE) : +toFixed(val / ONE_CRORE, digits)
    const isSingular = croreValue === 1

    let suffix: string
    if (isSmall) {
      suffix = applyCasing('Cr')
    } else {
      suffix = isSingular ? applyCasing('Crore') : applyCasing('Crores')
    }

    return `${RUPEE_SYMBOL}${formatNumberWithoutCurrency(croreValue)} ${suffix}`
  }

  if (val >= ONE_LAKH) {
    const lakhValue = isRound ? Math.round(val / ONE_LAKH) : +toFixed(val / ONE_LAKH, 2)
    const isSingular = lakhValue === 1

    let suffix: string
    if (isSmall) {
      suffix = applyCasing('L')
    } else {
      suffix = isSingular ? applyCasing('Lakh') : applyCasing('Lakhs')
    }

    return `${RUPEE_SYMBOL}${formatNumberWithoutCurrency(lakhValue)} ${suffix}`
  }

  return `${RUPEE_SYMBOL}${formatNumberWithoutCurrency(+val.toFixed(2))}`
}

// Write comment for the below method - convertEpochToDate with example input and output
/**
 * Converts an epoch timestamp to a date string
 * @param epoch - The epoch timestamp to convert
 * @param formatString - The format of the date string
 * @returns The formatted date string
 */
// Example input: 1723804800000
// Example output: '16 Aug 2025'
export function convertEpochToDate(epoch: number, formatString = '%_d %b %Y') {
  if (!epoch) return ''
  var format = d3.timeFormat(formatString)
  return format(new Date(epoch))
}

function getOrdinalSuffix(day: number): string {
  if (day >= 11 && day <= 13) return 'th'
  switch (day % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

/**
 * Converts an epoch timestamp to a date string with ordinal day (e.g. "3rd Mar 2025").
 */
export function convertEpochToDateWithOrdinalDay(epoch: number, monthYearFormat = '%b %Y'): string {
  if (!epoch) return ''
  const date = new Date(epoch)
  const day = date.getDate()
  const monthYear = d3.timeFormat(monthYearFormat)(date)
  return `${day}${getOrdinalSuffix(day)} ${monthYear}`
}

/**
 * Formats a date string to a given format
 * @param dateString - The date string to format
 * @param parseFormat - The format of the date string
 * @param convertFormat - The format of the date to return
 * @returns The formatted date string
 */
// Example input: '2025-08-16'
// Example output: '16 Aug 2025'
export function formatDate(dateString: string, parseFormat = '%Y-%m-%d', convertFormat = '%d %b %Y') {
  const parseDate = d3.timeParse(parseFormat)
  const date = parseDate(dateString)
  const format = d3.timeFormat(convertFormat)
  if (date) return format(date)
  return ''
}

/**
 * Checks if the current date is within a specified number of days from the start date
 * @param startDate - The start date as an epoch timestamp
 * @param days - The number of days to check (e.g., 7 for 7 days, 30 for 30 days)
 * @returns true if current date is within the specified days from start date, false otherwise
 * @example
 * isWithinDays(Date.now() - 5 * 24 * 60 * 60 * 1000, 7) // returns true (5 days ago, within 7 days)
 * isWithinDays(Date.now() - 10 * 24 * 60 * 60 * 1000, 7) // returns false (10 days ago, not within 7 days)
 */
export function isWithinDays(startDate: number | undefined, days: number): boolean {
  if (!startDate) return false
  const now = Date.now()
  const start = new Date(startDate).getTime()
  const daysInMs = days * 24 * 60 * 60 * 1000
  const difference = now - start
  return difference >= 0 && difference < daysInMs
}

/**
 * Formats a date range as "StartMonth - EndMonth plan"
 * @param startDate - The start date as an epoch timestamp
 * @param durationInMonths - The duration in months to add to the start date
 * @returns A formatted string like "Jan - Feb plan"
 * @example
 * formatPlanDateRange(1723804800000, 3) // returns "Aug - Nov plan"
 */
export function formatPlanDateRange(startDate: number, durationInMonths: number): string {
  if (!startDate) return ''

  const start = new Date(startDate)
  const startMonth = start.toLocaleDateString('en-US', { month: 'short' })

  // Create a new date object to avoid mutating the original
  const end = new Date(startDate)
  end.setMonth(end.getMonth() + durationInMonths)
  const endMonth = end.toLocaleDateString('en-US', { month: 'short' })

  return `${startMonth} - ${endMonth} plan`
}

export function toPascalCaseWithSpaces(str: string) {
  if (typeof str === 'string')
    return str
      .replace(/[^a-zA-Z0-9]+/g, ' ') // Replace non-alphanumeric characters with spaces
      .split(' ') // Split by spaces
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Capitalize each word
      .join(' ') // Join back with spaces between words

  return str
}

/**
 * Pluralizes a string by adding 's' if the condition is true or the number is not 1
 * @param str - The string to pluralize
 * @param condition - A boolean condition or a number. If number, pluralizes when !== 1
 * @returns The original string if condition is false/1, or the string with 's' appended
 * @example
 * plural('fund', 1) // returns 'fund'
 * plural('fund', 2) // returns 'funds'
 * plural('fund', true) // returns 'funds'
 * plural('fund', false) // returns 'fund'
 */
export function plural(str: string, condition: boolean | number): string {
  const shouldPluralize = typeof condition === 'number' ? condition !== 1 : condition
  return shouldPluralize ? `${str}s` : str
}

export function isDecimal(value: number) {
  return Number.isFinite(value) && value % 1 !== 0
}

export function convertUnitAndValue({
  value,
  unit,
  format,
  formatValue = true
}: {
  value: number
  unit: `${UNITS}`
  format?: {
    casing: 'LOWER' | 'PASCAL' | 'UPPER'
    type: 'LONG' | 'SHORT'
  }
  formatValue?: boolean
}) {
  const getPlural = (val: number, suffix: string) => {
    return `${val}${suffix}${val === 1 ? '' : 's'}`
  }

  const formatter: { [x in `${UNITS}`]: () => string } = {
    [UNITS.PERCENTAGE]: () => {
      if (!formatValue) return `${value}%`
      if (!value) return '0%'
      let fixedDigits = isDecimal(value) ? 2 : 0
      return `${value.toFixed(fixedDigits)}%`
    },
    [UNITS.RUPEES]: () => convertNumberToCurrency({ amount: value }),
    [UNITS.MONTH]: () => {
      if (format) {
        const { casing, type } = format
        if (casing === 'LOWER' && type === 'LONG') return `${getPlural(value, ' month')}`
        else if (casing === 'LOWER' && type === 'SHORT') `${value}m`
        else if (casing === 'PASCAL' && type === 'LONG') return `${getPlural(value, ' Month')}`
        else if (casing === 'PASCAL' && type === 'SHORT') return `${value}M`
      }
      return `${getPlural(value, ' month')}`
    },
    [UNITS.YEAR]: () => {
      if (format) {
        const { casing, type } = format
        if (casing === 'LOWER' && type === 'LONG') return `${getPlural(value, ' year')}`
        else if (casing === 'LOWER' && type === 'SHORT') `${value}y`
        else if (casing === 'PASCAL' && type === 'LONG') return `${getPlural(value, ' Year')}`
        else if (casing === 'PASCAL' && type === 'SHORT') return `${value}Y`
      }
      return `${getPlural(value, ' year')}`
    },
    [UNITS.DAY]: () => {
      if (format) {
        const { casing, type } = format
        if (casing === 'LOWER' && type === 'LONG') return `${getPlural(value, ' day')}`
        else if (casing === 'LOWER' && type === 'SHORT') `${value}d`
        else if (casing === 'PASCAL' && type === 'LONG') return `${getPlural(value, ' Day')}`
        else if (casing === 'PASCAL' && type === 'SHORT') return `${value}D`
      }
      return `${getPlural(value, ' day')}`
    },
    [UNITS.ALL]: () => {
      if (format) {
        const { casing } = format
        if (casing === 'LOWER') return `all`
        else if (casing === 'PASCAL') return `ALL`
        else if (casing === 'UPPER') return `ALL`
      }
      return `ALL`
    }
  }

  if (unit) return formatter?.[unit]?.() ?? '-'

  return '-'
}

export function opacityElementByAttribute(attribute: string, value: string | undefined, opacity: '0' | '1'): void {
  const elements = document?.querySelectorAll(`[${attribute}="${value}"]`)
  elements?.forEach((element: any) => {
    element?.style?.setProperty('opacity', `${opacity}`)
  })
}

export function getTickFormat(tenure: string) {
  const isShortTermTenure = ['1M', '3M'].includes(tenure)
  const tickFormat = isShortTermTenure ? '%d %b' : '%b %y'
  return tickFormat
}

export async function copyToClipboard(textToCopy: string) {
  if (navigator?.clipboard && window?.isSecureContext) {
    await navigator.clipboard.writeText(textToCopy)
  } else {
    const textArea = document.createElement('textarea')
    textArea.value = textToCopy

    textArea.style.position = 'absolute'
    textArea.style.left = '-999999px'

    document.body.prepend(textArea)
    textArea.select()

    try {
      document?.execCommand('copy')
    } catch (error) {
      console.error(error)
    } finally {
      textArea.remove()
    }
  }
}

export const getAppOStype = (): string => {
  const authHeaders = getAuthHeaders()
  return authHeaders?.['os-type'] ?? ''
}

export const getUpdateAppCTA = () => {
  const osType = getAppOStype()
  return osType?.toLowerCase() === 'ios' ? iosUpdateApp : androidUpdateApp
}

export const convertMillisecondsToMinutesAndSeconds = (milliseconds: number) => {
  const totalSeconds = Math.floor(milliseconds / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const secondsLeft = Math.floor(totalSeconds % 60)
  return `${minutes}:${String(secondsLeft).padStart(2, '0')}`
}

export const monthLookup = {
  '1': {
    long: 'January',
    short: 'Jan'
  },
  '2': {
    long: 'February',
    short: 'Feb'
  },
  '3': {
    long: 'March',
    short: 'Mar'
  },
  '4': {
    long: 'April',
    short: 'Apr'
  },
  '5': {
    long: 'May',
    short: 'May'
  },
  '6': {
    long: 'June',
    short: 'Jun'
  },
  '7': {
    long: 'July',
    short: 'Jul'
  },
  '8': {
    long: 'August',
    short: 'Aug'
  },
  '9': {
    long: 'September',
    short: 'Sep'
  },
  '10': {
    long: 'October',
    short: 'Oct'
  },
  '11': {
    long: 'November',
    short: 'Nov'
  },
  '12': {
    long: 'December',
    short: 'Dec'
  }
}

export const manualDelay = (ms: number) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(undefined)
    }, ms)
  })
}

export const isIOS = () => {
  if (typeof window === 'undefined') return false
  const ua = navigator?.userAgent || ''
  const platform = navigator?.platform || ''

  // Check for explicit iOS devices in user agent
  const isIOSDevice = /iPad|iPhone|iPod/.test(ua)

  // Check for iPad Pro with iOS 13+ (reports as Mac but has specific characteristics)
  const isIPadPro =
    platform === 'MacIntel' && navigator?.maxTouchPoints > 1 && (ua.includes('iPad') || ua.includes('Mobile'))

  // Additional check: iOS devices typically have specific WebKit versions
  const hasIOSWebKit = ua.includes('Version/') && ua.includes('Mobile/') && ua.includes('Safari/')

  return isIOSDevice || isIPadPro || hasIOSWebKit
}

export const isSafari = () => {
  if (typeof window === 'undefined') return false
  const ua = navigator?.userAgent || ''

  // Safari has Safari in user agent but NOT Chrome, NOT Edge, NOT Firefox
  const hasSafari = ua.includes('Safari')
  const hasChrome = ua.includes('Chrome')
  const hasEdge = ua.includes('Edg')
  const hasFirefox = ua.includes('Firefox')

  // Safari is the only browser that has Safari but not Chrome/Edge/Firefox
  return hasSafari && !hasChrome && !hasEdge && !hasFirefox
}

export const isObjectEmpty = (value: unknown): boolean => {
  if (value == null) {
    return true
  }

  if (typeof value !== 'object' || Array.isArray(value)) {
    return false
  }

  return Object.keys(value).length === 0
}
